import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";

// ─── CONFIGURACIÓN EMAILJS ───────────────────────────────────────────────────
// 1. Crea cuenta gratis en https://www.emailjs.com
// 2. Crea un Email Service (Gmail, Outlook, etc.)
// 3. Crea un Email Template con las variables: {{name}}, {{company}},
//    {{email}}, {{phone}}, {{service}}, {{message}}
// 4. Reemplaza los tres valores de abajo con los tuyos
const EMAILJS_SERVICE_ID  = "service_l7eai8l";
const EMAILJS_TEMPLATE_ID = "template_j1mnlsh";
const EMAILJS_PUBLIC_KEY  = "HbK521iOT2DPZ1giU";
// ─────────────────────────────────────────────────────────────────────────────

const SERVICES = [
  "Videovigilancia",
  "Control de acceso",
  "Redes empresariales",
  "Fibra óptica",
  "HVAC industrial",
  "Construcción e infraestructura",
  "Otro",
];

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service: "",
  message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => {
    setForm((p) => ({ ...p, [k]: e.target.value }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Ingrese su nombre completo.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      er.email = "Correo electrónico inválido.";
    if (form.phone.replace(/\D/g, "").length < 7)
      er.phone = "Teléfono inválido.";
    if (form.message.trim().length < 5)
      er.message = "Por favor describa brevemente su proyecto.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Revise los campos del formulario.");
      return;
    }
    setSubmitting(true);
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          name:    form.name.trim(),
          company: form.company.trim() || "No especificada",
          email:   form.email.trim(),
          phone:   form.phone.trim(),
          service: form.service || "No especificado",
          message: form.message.trim(),
        },
        EMAILJS_PUBLIC_KEY
      );
      toast.success("¡Mensaje enviado! Le contactaremos en menos de 24h.");
      setForm(initial);
    } catch (err) {
      console.error("EmailJS error:", err);
      toast.error("No fue posible enviar el mensaje. Intente nuevamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 border-t border-white/10 bg-[#181C24]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left info */}
          <div className="lg:col-span-5 reveal">
            <span className="section-tag">05 / Contacto</span>
            <h2 className="mt-6 font-display font-extrabold text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[0.98]">
              Hablemos de su próximo{" "}
              <span className="text-[#F8BC33]">proyecto industrial</span>.
            </h2>
            <p className="mt-6 text-white/65 leading-relaxed">
              Cotizaciones técnicas en menos de 24 horas hábiles. Visitas a
              sitio sin costo dentro de la zona metropolitana de Saltillo.
            </p>

            <div className="mt-10 space-y-px bg-white/10 border border-white/10">
              <a
                href="tel:+528440000000"
                data-testid="contact-phone"
                className="flex items-center gap-5 bg-[#181C24] hover:bg-[#212731] transition-colors p-6"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 border border-white/15 text-[#F8BC33]">
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <div>
                  <div className="font-mono-tech text-[10px] tracking-widest uppercase text-white/40">
                    Teléfono
                  </div>
                  <div className="font-display font-bold text-lg">
                    +52 844 000 0000
                  </div>
                </div>
              </a>
              <a
                href="mailto:contacto@infratelco.mx"
                data-testid="contact-email"
                className="flex items-center gap-5 bg-[#181C24] hover:bg-[#212731] transition-colors p-6"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 border border-white/15 text-[#F8BC33]">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <div>
                  <div className="font-mono-tech text-[10px] tracking-widest uppercase text-white/40">
                    Correo
                  </div>
                  <div className="font-display font-bold text-lg">
                    contacto@infratelco.mx
                  </div>
                </div>
              </a>
              <div
                data-testid="contact-address"
                className="flex items-center gap-5 bg-[#181C24] p-6"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 border border-white/15 text-white/70">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <div>
                  <div className="font-mono-tech text-[10px] tracking-widest uppercase text-white/40">
                    Oficina
                  </div>
                  <div className="font-display font-bold text-lg">
                    Saltillo, Coahuila · México
                  </div>
                </div>
              </div>
            </div>

            <a
              href="https://wa.me/528442045717?text=Hola%2C%20me%20gustar%C3%ADa%20solicitar%20una%20cotizaci%C3%B3n."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="mt-8 flex items-center gap-5 border border-[#25D366]/30 bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors p-6 group"
            >
              <span className="inline-flex items-center justify-center w-14 h-14 border border-[#25D366]/40 bg-[#25D366]/15 text-[#25D366] flex-shrink-0">
                <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.532 5.855L.057 23.428a.75.75 0 00.916.916l5.573-1.475A11.943 11.943 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.694 9.694 0 01-4.964-1.364l-.355-.212-3.684.974.986-3.595-.231-.369A9.694 9.694 0 012.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/>
                </svg>
              </span>
              <div className="flex-1">
                <div className="font-mono-tech text-[10px] tracking-widest uppercase text-[#25D366]/70">
                  WhatsApp · Respuesta inmediata
                </div>
                <div className="font-display font-bold text-xl text-white mt-1">
                  +52 844 204 5717
                </div>
                <div className="text-white/50 text-sm mt-0.5">
                  Toca para abrir una conversación directa
                </div>
              </div>
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#25D366]/60 group-hover:translate-x-1 transition-transform flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
              </svg>
            </a>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 reveal reveal-delay-1">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-white/10 bg-[#212731] p-6 sm:p-10 space-y-6"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <Field
                  label="Nombre completo *"
                  testId="contact-input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Juan Pérez"
                  error={errors.name}
                />
                <Field
                  label="Empresa"
                  testId="contact-input-company"
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Industria ABC S.A. de C.V."
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field
                  label="Correo electrónico *"
                  testId="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="contacto@empresa.com"
                  error={errors.email}
                />
                <Field
                  label="Teléfono *"
                  testId="contact-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+52 844 000 0000"
                  error={errors.phone}
                />
              </div>

              <div>
                <label className="font-mono-tech text-[10px] tracking-widest uppercase text-white/40">
                  Servicio de interés
                </label>
                <select
                  data-testid="contact-input-service"
                  value={form.service}
                  onChange={update("service")}
                  className="industrial-input w-full mt-2 px-4 py-3 text-white text-sm focus:outline-none"
                >
                  <option value="">Seleccione un servicio</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s} className="bg-[#212731]">
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-mono-tech text-[10px] tracking-widest uppercase text-white/40">
                  Mensaje *
                </label>
                <textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Describa brevemente su proyecto, planta, alcance estimado o requerimiento."
                  className="industrial-input w-full mt-2 px-4 py-3 text-sm focus:outline-none resize-none"
                />
                {errors.message && (
                  <p className="mt-2 text-xs text-[#F8BC33]" data-testid="error-message">
                    {errors.message}
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                <p className="text-xs text-white/45 leading-relaxed">
                  Al enviar este formulario acepta nuestra política de
                  privacidad y el uso de sus datos para fines de contacto
                  comercial.
                </p>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="btn-supernova inline-flex items-center justify-center gap-3 px-7 py-4 text-sm font-bold uppercase tracking-wider disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
                    </>
                  ) : (
                    <>
                      Enviar mensaje <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testId, value, onChange, placeholder, type = "text", error }) => (
  <div>
    <label className="font-mono-tech text-[10px] tracking-widest uppercase text-white/40">
      {label}
    </label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="industrial-input w-full mt-2 px-4 py-3 text-sm focus:outline-none"
    />
    {error && (
      <p className="mt-2 text-xs text-[#F8BC33]" data-testid={`error-${testId}`}>
        {error}
      </p>
    )}
  </div>
);

export default Contact;
